import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Content,
  FormContent,
  FormBlock,
  Group,
  Icon,
  InputValue,
  Item,
  Label,
  LinkContent,
  LinkIcon,
  ProfileView,
  SubBlock,
  Text,
  Title
} from './styles';
import Button from '../../../component/button';
import { getInvitationDetails, signUp } from '../../../services/authServices';
import { useHistory, useParams } from 'react-router-dom';
import { APP_INIT_RESPONSE_TYPE, BUTTON_TYPE } from '../../../global/constants';
import { InviteInputInterface, SignUpPayloadInterface } from '../../../interfaces/AuthInterface';
import AppLogo from '../../../component/appLogo';
import { getCookie, isEmpty } from '../../../helpers/common';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { captureException } from '../../../services/logService';
import AppLoading from '../../../component/loading/appLoading';
import LayoutWithoutNavbar from '../../../component/layoutWithoutNavbar';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { ROUTES } from '../../../routes/urls';

const SignupJoin: React.FC = () => {
  // States
  const [referCodeValue, setReferCodeValue] = useState('');
  const [initLoading, setInitLoading] = useState(false);
  const [linkExpire, setLinkExpire] = useState(false);
  const [inviteInput, setInviteInput] = useState<InviteInputInterface>({
    companyName: '',
    email: '',
    invitedBy: '',
    name: '',
    password: ''
  });

  //mobx variable
  const { appStore } = rootStore;
  const { authLoading } = appStore;

  // Other variables
  const params: { id: string } = useParams();
  const history = useHistory();

  // load invitation details
  const loadInvitationDetails = useCallback(async () => {
    try {
      setInitLoading(true);
      const response = await getInvitationDetails(params?.id);
      if (!response) {
        setLinkExpire(true);
      } else {
        setInviteInput((prev) => ({
          ...prev,
          ...response
        }));
      }
      const cookieValue = getCookie('cross_domain_referral');
      if (cookieValue) setReferCodeValue(cookieValue);
    } catch (e) {
      captureException(e);
    } finally {
      setInitLoading(false);
    }
  }, [params?.id]);

  useEffect(() => {
    if (!inviteInput?.email) {
      loadInvitationDetails();
    }
  }, [inviteInput?.email, loadInvitationDetails]);

  // handle input field change
  const onChangeInputField = useCallback((propsName: string, value: string) => {
    setInviteInput((prev) => ({
      ...prev,
      [propsName]: value
    }));
  }, []);

  // for join button click
  const onClickJoin = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      let payload: SignUpPayloadInterface = {
        name: inviteInput?.name,
        email: inviteInput?.email,
        password: inviteInput?.password,
        confirmPassword: inviteInput?.password
      };
      if (!isEmpty(referCodeValue)) payload.referCode = referCodeValue;
      const result = await signUp(payload);
      if (result && result.type === APP_INIT_RESPONSE_TYPE.REDIRECT && result.path) {
        history.push(result.path);
      } else if (result && result.type === APP_INIT_RESPONSE_TYPE.SUCCESS) {
        history.push(ROUTES.INBOX);
      }
    },
    [history, inviteInput?.email, inviteInput?.name, inviteInput?.password, referCodeValue]
  );

  return (
    <>
      <LayoutWithoutNavbar>
        <Group>
          {initLoading && <AppLoading />}
          {!initLoading && (
            <Content onSubmit={onClickJoin}>
              <Icon>
                <AppLogo height={60} />
              </Icon>
              {linkExpire ? (
                <Box>
                  <LinkIcon>
                    <SVGIcon name='invitation-link-icon' width='36' height='36' viewBox='0 0 36 36' />
                  </LinkIcon>
                  <LinkContent>
                    <h2>Oops! Your invitation link expired.</h2>
                    <p>Contact the admin and send them a message for new invitation link.</p>
                  </LinkContent>
                  <Button variant={BUTTON_TYPE.PRIMARY} onClick={() => history.push(ROUTES.INBOX)} title='Go to Teamcamp' />
                </Box>
              ) : (
                <>
                  <Title>
                    {inviteInput?.invitedBy} Invite you to join {inviteInput?.companyName}
                  </Title>
                  <FormContent>
                    <FormBlock>
                      <ProfileView>
                        <Text>First, tell us a bit about your company.</Text>
                      </ProfileView>

                      <SubBlock>
                        <Item>
                          <Label>Name</Label>
                          <InputValue
                            typeof='text'
                            placeholder='Name'
                            value={inviteInput?.name}
                            onChange={(e) => onChangeInputField('name', e.target.value)}
                          />
                        </Item>
                        <Item>
                          <Label>Email</Label>
                          <InputValue
                            typeof='text'
                            disabled={true}
                            placeholder='Enter your Email'
                            value={inviteInput?.email}
                            readOnly={true}
                          />
                        </Item>

                        <Item>
                          <Label>Password</Label>
                          <InputValue
                            type='password'
                            placeholder='Enter your Password'
                            className='password'
                            size={10}
                            value={inviteInput?.password}
                            onChange={(e) => onChangeInputField('password', e.target.value)}
                          />
                        </Item>
                      </SubBlock>
                    </FormBlock>
                    <div className='joinButton'>
                      <Button
                        buttonType='submit'
                        variant={BUTTON_TYPE.PRIMARY}
                        title={`Join ${inviteInput?.companyName}`}
                        disabled={authLoading}
                        isLoading={authLoading}
                      />
                    </div>
                  </FormContent>
                </>
              )}
            </Content>
          )}
        </Group>
      </LayoutWithoutNavbar>
    </>
  );
};
export default observer(SignupJoin);
